<template>
<div class="chapter">
<div class="inner_page">
<p class="chap-title"><span>2</span></p>
<p class="normal">Harry Bannerman stood at the bar in the club car of the 5:29. In his hand was a bourbon and water, his second since leaving Grand Central Station twenty minutes earlier. Harry was not ordinarily a bourbon drinker—scotch was his usual tipple—but he had discovered that bourbon made him more drunk more quickly. That, in recent months, had become an important consideration.</p>
<p class="indent">Harry was a typical commuter of Putnam’s Landing, Connecticut, which is to say that he was between 35 and 40 in age, married, the father of three children, the owner of a house, a first mortgage, a second mortgage, a gray flannel suit, a bald spot, and a vague feeling of discontent.</p>
<p class="indent">Though he loved his wife and children, though he enjoyed his house and had hopes of reforesting his bald spot, though he was, all in all, not dissatisfied with his lot, just the same, from time to time, a sort of helpless feeling took hold of him—a feeling that he had no control over the forces that shaped his life—that he was merely a puppet in the hands of some dimly understood power. Namely, his wife.</p>
<p class="indent">Make no mistake: he loved her. Grace was handsome, fair, supple, and bright, and he had wanted to marry her the minute he had clapped eyes on her. It had been right after World War II. Harry had just been mustered out of the Navy and had returned to New York where he found a job on the “Talk of the Town” section of The New Yorker. Grace was an assistant in the same department. When she saw Harry walk in wearing his prewar civvies, his wrists and ankles sticking out like Huck Finn’s, she promptly burst into laughter. But it was warm, friendly laughter, and Harry did not mind a bit. He told her that if she <i>really</i> wanted some laughs, she should see him in his tuxedo. So they went to dinner that night, and then they had a lot more dinners and rode in hansom cabs and listened to jazz at Condon’s and took trips on the Hudson River Day Line and pressed their noses against Cartier’s window and got married.</p>
<p class="indent">Harry’s idea of married life was simple: you rented an apartment in Greenwich Village and sat on a pouf and listened to Rodgers and Hart records and drank wine from wicker covered bottles and held each other very tight.</p>
<p class="indent">Which is just how it was for the better part of a year. They lived in a high-ceilinged two-room apartment on Bank Street with a mattress, a box spring, a corduroy throw, a red and blue pouf, an electric percolator, a hot plate, and a phonograph without a changer. That was the only thing Harry lacked to make his happiness complete—a changer for the phonograph.</p>
<p class="indent">Grace’s ambitions were rather larger. “Darling,” she said to Harry one night, “don’t you think people ought to start their families when they’re young so they can grow up with their children?”</p>
<p class="indent">“Yes, I suppose so,” he replied casually, and the next thing he remembered, his son Dan was upon him.</p>
<p class="indent">(That was Grace’s idea of a conference. She was always coming up to Harry and saying something like “Wouldn’t it be nice to have panelling in the basement?” or “Don’t you wish we had more closet space?” and he would answer absently “Yeah,” or “Uh-huh,” and the next time he came home from work, the house was teeming with carpenters.)</p>
<p class="indent">So now they had their son Dan. He did not do much for the first six months except cry and spill things, including a bottle of cod liver oil on Harry’s bed, and if you have never slept on a mattress reeking of cod liver oil, you have never known anguish. But Harry got a new mattress and eventually the boy turned fat and pink and no trouble to anyone.</p>
<p class="indent">One night after this satisfactory child had been put to bed and Harry and Grace were curled up on the red and blue pouf, she said to him, “You know, it must be terribly lonely to be an only child. Don’t you think so?”</p>
<p class="indent">“I guess it is,” he replied absently, and before you could say twilight sleep, he was the father of another boy.</p>
<p class="indent">After Bud (for that was his name) joined the family, there were no longer enough poufs to go around, so, of course, they had to move to a bigger place. “Why not buy a house in the country?” suggested Grace. “It’s just as cheap as paying rent, and it’ll be so wonderful for the children.”</p>
<p class="indent">“Well—” said Harry, and while he was scratching his head, he became the owner of a house on a hill in Putnam’s Landing, Connecticut.</p>
<p class="indent">For Grace and Putnam’s Landing, it was love at first sight. Almost before she was unpacked, she had had another baby, bought a large brown dog, joined the PTA, the League of Women Voters, the Women’s Club, the Red Cross, the Nurses Aids, the Mental Health Society, and the Town Planning Commission. “How wonderful,” she would cry, slinging Dan on one hip and Bud on the other, tucking young Peter under her arm, putting the dog beneath on a leash, and rushing out on errands of mercy, “to live in a town with real community spirit!”</p>
<p class="indent">Harry’s enthusiasm for Putnam’s Landing was kept under somewhat tighter control. He liked the place, mind you. It did have, as Grace said, real community spirit, and the people were interesting—writers, artists, actors, ad men, TV executives, and other such animated types—and there was a pleasant patina of New England upon the winding lanes and rolling land. But living in Putnam’s Landing was a blessing not entirely unmixed. For one thing, it cost more money than Harry was making. For another, it required more hours than there were in a day.</p>
<p class="indent">Once, on a dullish afternoon at the office, Harry set down a time-table of a typical day in his life. It looked like this:</p>
<table cellpadding="2" cellspacing="0" width="100%">
<tbody><tr>
<td style="width:20%" valign="top">  6:30 <span class="smallcaps">A.M</span>.</td>
<td style="width:80%" valign="top">Rise, shave, shower, breakfast.</td>
</tr>
<tr>
<td valign="top">  7:00</td>
<td valign="top">Wake Grace to drive me to station.</td>
</tr>
<tr>
<td valign="top">  7:10</td>
<td valign="top">Wake Grace again.</td>
</tr>
<tr>
<td valign="top">  7:16</td>
<td valign="top">Grace starts driving me to station.</td>
</tr>
<tr>
<td valign="top">  7:20</td>
<td valign="top">Grace scrapes fender on milk truck.</td>
</tr>
<tr>
<td valign="top">  7:36</td>
<td valign="top">Arrive station.</td>
</tr>
<tr>
<td valign="top">  7:37</td>
<td valign="top">Board train for New York.</td>
</tr>
<tr>
<td valign="top">  8:45</td>
<td valign="top">Arrive Grand Central.</td>
</tr>
<tr>
<td valign="top">  9:00</td>
<td valign="top">Arrive New Yorker Magazine.</td>
</tr>
<tr>
<td valign="top">  5:18 <span class="smallcaps">P.M</span>.</td>
<td valign="top">Leave New Yorker Magazine.</td>
</tr>
<tr>
<td valign="top">  5:29</td>
<td valign="top">Board train to Putnam’s Landing.</td>
</tr>
<tr>
<td valign="top">  6:32</td>
<td valign="top">Arrive Putnam’s Landing. Grace waiting at station.</td>
</tr>
<tr>
<td valign="top">  6:51</td>
<td valign="top">Traffic jam at station untangles. We start home.</td>
</tr>
<tr>
<td valign="top">  6:52</td>
<td valign="top">Grace tells me sump pump broken.</td>
</tr>
<tr>
<td valign="top">  6:56</td>
<td valign="top">I ask Grace what is sump pump.</td>
</tr>
<tr>
<td valign="top">  6:57</td>
<td valign="top">Grace tells me sump pump is pump that pumps sump.</td>
</tr>
<tr>
<td valign="top">  6:58</td>
<td valign="top">I say Oh.</td>
</tr>
<tr>
<td valign="top">  7:00</td>
<td valign="top">Grace tells me Bud swallowed penny.</td>
</tr>
<tr>
<td valign="top">  7:02</td>
<td valign="top">Grace tells me Dan called his teacher an “old poop.”</td>
</tr>
<tr>
<td valign="top">  7:04</td>
<td valign="top">Grace tells me Peter is allergic to the mailman.</td>
</tr>
<tr>
<td valign="top">  7:06</td>
<td valign="top">Grace tells me she signed me up to work all day Saturday in Bingo tent at Womans Club Bazaar.</td>
</tr>
<tr>
<td valign="top">  7:12</td>
<td valign="top">Arrive home.</td>
</tr>
<tr>
<td valign="top">  7:13</td>
<td valign="top">Dan, aged 8, Bud, aged 6, and Peter, aged 4, looking at television. Dan and Bud want to look at Looney Tunes. Peter wants to look at John Cameron Swayze. (?) Grace rules in favor of Peter. Bud swallows another penny.</td>
</tr>
<tr>
<td valign="top">  7:30</td>
<td valign="top">Grace puts children to bed. I go out on lawn to pick up toys.</td>
</tr>
<tr>
<td valign="top">  7:38</td>
<td valign="top">Dinner.</td>
</tr>
<tr>
<td valign="top">  8:01</td>
<td valign="top">Mrs. Epperson, baby sitter, rings doorbell. I ask Grace what we need with baby sitter. Grace says tonight is PTA meeting. I remind Grace we just went to PTA meeting three days ago. Grace says that was regular meeting, tonight is special emergency protest meeting. We go to special emergency protest meeting.</td>
</tr>
<tr>
<td valign="top">  8:32</td>
<td valign="top">Arrive special emergency protest meeting. Special emergency protest seems to be about a hole in the school playground. Chairman of Board of Education, a conservative Yankee type, says no appropriation in budget for fixing hole. Grace rises and demands special appropriation. Chairman of Board calls this creeping socialism. I doze off.</td>
</tr>
<tr>
<td valign="top">  9:51</td>
<td valign="top">Grace jams elbow in my ribs, wakes me to vote on motion to refer hole to Special Committee to Study Hole in Playground. Motion carried.</td>
</tr>
<tr>
<td valign="top">  9:52</td>
<td valign="top">Meeting adjourned.</td>
</tr>
<tr>
<td valign="top">  9:53</td>
<td valign="top">Grace and I go to Fatso’s Diner with O’Sheels and Steinbergs, fellow PTA members. Women discuss hole further. Men yawn.</td>
</tr>
<tr>
<td valign="top">10:48</td>
<td valign="top">Leave Fatso’s Diner.</td>
</tr>
<tr>
<td valign="top">11:25</td>
<td valign="top">Arrive home. Grace asks Mrs. Epperson, baby sitter, if everything all right. Mrs. Epperson says Bud woke up once and started crying but she gave him some pennies and he went back to sleep.</td>
</tr>
<tr>
<td valign="top">11:58</td>
<td valign="top">Grace and I go to bed.</td>
</tr>
<tr>
<td valign="top">12:04</td>
<td valign="top">Grace says she hears animals around garbage can. I go out.</td>
</tr>
<tr>
<td valign="top">12:05</td>
<td valign="top">Grace is right. There <i>are</i> animals around garbage can. I go back in.</td>
</tr>
<tr>
<td valign="top">12:53</td>
<td valign="top">Animals finish garbage.</td>
</tr>
<tr>
<td valign="top">  1:10</td>
<td valign="top">I sleep.</td>
</tr>
</tbody></table>
<p class="indent">And so passed the days of Harry Bannerman’s years. If it wasn’t a meeting, a caucus, a rally, or a lecture, then it was a quiet evening at home licking envelopes. Or else it was a party where you ate cubes of cheese on toothpicks and talked about plywood, mortgages, mulches, and children. Or it was amateur theatricals. Or ringing doorbells for worthy causes. Or umpiring Little League games. Or setting tulip bulbs. Or sticking decals on cribs. Or trimming hedges. Or reading Dr. Spock. Or barbecuing hamburgers. Or increasing your life insurance. Or doing anything in the whole wide world except sitting on a pouf with a soft and loving girl and listening to Rodgers and Hart.</p>
<p class="indent">It was more and more on Harry’s mind—the pouf, the phonograph records, the long, languorous nights. He would look at Grace in a nubby tweed skirt and a cardigan with the sleeves pushed up, rushing about dispensing civic virtue, wisps of hair coming loose, her seams crooked—and he would remember another Grace in pink velvet lounging pajamas, curled up like a kitten next to him on the pouf, in one hand a cigarette lazily trailing smoke, the other hand doing talented things to the back of his neck.</p>
<p class="indent">He would look at his house—the leaks, the squeaks, the chips, the cracks, the things that had to be repaired, recovered, rewired, replaced, remodeled—and he would recall the days when all you did when something went wrong was phone the landlord.</p>
<p class="indent">He would look at his children. He would watch them devouring sides of beef and crates of eggs; poking toes through stockings and elbows through sweaters; littering the yard with balls, bats, bicycles, tricycles, scooters, blocks, crayons, paints, tops, hoops, marbles, bows, arrows, darts, guns, and key bits of jigsaw puzzles; trailing mud on the rugs; breaking off the corners of playing cards; eating watermelon in bed; nailing pictures of athletes to walls; leaving black rings in the tub; getting carsick—he would observe this arresting pageant and he would think, “Yes, they are fine children, they are normal, I love them very much, and I will guard and keep them always … But, oh, how sweet and satisfactory those golden days on the pouf!”</p>
<p class="indenta">Sighing, Harry ordered another bourbon. “You know something?” he said to the bartender. “You know who saved more marriages in Fairfield County than the church, the state, and the psychiatrists all put together?”</p>
<p class="indent">“Who?” asked the bartender.</p>
<p class="indent">“Pat McGinnis,” said Harry.</p>
<p class="indent">The bartender looked at him incredulously. “You mean the Pat McGinnis who used to run the New Haven Railroad?”</p>
<p class="indent">“Yes, sir,” said Harry vehemently. “Oh, I know he ran a godawful railroad. The trains were late and the cars were filthy and the service was miserable. But, by God, he did one thing that every wife in Fairfield County ought to get down on her knees and thank him for every night!”</p>
<p class="indent">“What’s that?”</p>
<p class="indent">“He put club cars in the commuter trains,” said Harry. “Do you think us poor slobs could face what’s waiting for us at home if we had to get off this train sober?”</p>
<p class="indent">“Putnam’s Landing!” called the conductor, opening the door of the car. “This stop is Putnam’s Landing.”</p>
<p class="indent">Harry tossed off his bourbon and, less anesthetized than he would have liked, walked slowly toward the door.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter002.vue?c10b6668